import { useEffect, useState } from "react";
import { singletonHook } from "react-singleton-hook";

// Interfaces
import { StripePrice } from "../interfaces/Stripe/StripePrice";
import { StripeCoupon } from "../interfaces/Stripe/StripeCoupon";

// Settings & constants
import { USE_HARDCODED_SUBSCRIPTION_PRICES } from "../settings";
import { getSubscriptionPlans, getSubscriptionStripePrices } from "../catalog/subscriptionPlans";
import { stripeCallableNames, stripeProductIds } from "../config/billing";

// Firebase
import { functions } from "../firebase/firebase";
import { httpsCallable } from "firebase/functions";
import { useFetchCoach } from "./useFetchCoach";

// UPDATED: The initial state object for singletonHook
const initState = {
    prices: null,
    pricesFetched: false,
    pricesError: null,
    couponFetched: false,
    fetchCouponDetails: async (promoCode: string) => null as StripeCoupon | null, // <-- CORRECTED: The function should return a value, not a type
};

const reconcileStripePrices = (fetchedPrices: StripePrice[]) => {
    const catalogPlans = getSubscriptionPlans();
    const fetchedPriceMap = new Map(fetchedPrices.map(price => [price.priceId, price]));
    const missingPriceIds = catalogPlans.filter(plan => !fetchedPriceMap.has(plan.priceId)).map(plan => plan.priceId);

    if (missingPriceIds.length > 0) {
        return {
            prices: null,
            error: `Missing Stripe prices for configured plan IDs: ${missingPriceIds.join(", ")}`,
        };
    }

    catalogPlans.forEach(plan => {
        const fetchedPrice = fetchedPriceMap.get(plan.priceId);

        if (!fetchedPrice) {
            return;
        }

        if (
            fetchedPrice.unitAmount !== plan.unitAmount ||
            fetchedPrice.currency.toLowerCase() !== plan.currency.toLowerCase() ||
            fetchedPrice.interval !== plan.interval ||
            fetchedPrice.intervalCount !== plan.intervalCount
        ) {
            console.warn("(useFetchSubscriptionPlans) Stripe price metadata differs from catalog.", {
                priceId: plan.priceId,
                catalog: {
                    unitAmount: plan.unitAmount,
                    currency: plan.currency,
                    interval: plan.interval,
                    intervalCount: plan.intervalCount,
                },
                stripe: fetchedPrice,
            });
        }
    });

    return {
        prices: getSubscriptionStripePrices(),
        error: null,
    };
};

const useFetchSubscriptionPlansImpl = () => {
    // consts
    const hardcodedPrices = getSubscriptionStripePrices();

    const debug = true;

    // Hooks
    const { authUser, currentCoach } = useFetchCoach();

    // UPDATED: States are now individual variables
    const [prices, setPrices] = useState<StripePrice[] | null>(null);
    const [coupon, setCoupon] = useState<StripeCoupon | null>(null);
    const [pricesFetched, setPricesFetched] = useState<boolean>(false);
    const [pricesError, setPricesError] = useState<string | null>(null);
    const [couponFetched, setCouponFetched] = useState<boolean>(true);

    // Function to fetch prices from Stripe
    const fetchProductPrices = async (productId: string) => {
        if (USE_HARDCODED_SUBSCRIPTION_PRICES) {
            setPrices(hardcodedPrices);
            setPricesFetched(true);
            setPricesError(null);
            return;
        }

        try {
            setPricesFetched(false);
            setPricesError(null);

            const getProductPrices = httpsCallable(functions, stripeCallableNames.getProductPrices);

            const result = await getProductPrices({ productId: productId });
            const pricesData = (result.data as any).prices as StripePrice[];

            if (debug) {
                console.log("(useFetchSubscriptionPlans) -> fetchProductPrices() -> result.data:", result.data);
            }

            const reconciledResult = reconcileStripePrices(pricesData);

            if (reconciledResult.error) {
                console.error("(useFetchSubscriptionPlans) -> fetchProductPrices()", reconciledResult.error);
                setPrices(null);
                setPricesError(reconciledResult.error);
                setPricesFetched(true);
                return;
            }

            setPrices(reconciledResult.prices);
            setPricesFetched(true);
            setPricesError(null);
        } catch (error: any) {
            console.error("(useFetchSubscriptionPlans) -> fetchProductPrices()", error.code, error.message);
            setPrices(null);
            setPricesFetched(false);
            setPricesError(error.message || "Unable to fetch subscription pricing.");
        }
    };

    // New function to fetch coupon details
    const fetchCouponDetails = async (promoCode: string) => {
        try {
            setCouponFetched(false);

            const getCouponDetails = httpsCallable(functions, stripeCallableNames.getCouponDetails);

            const result = await getCouponDetails({ promoCode });
            const couponData = (result.data as any).data as StripeCoupon;

            setCoupon(couponData); // <-- ADDED: Set the coupon state here
            setCouponFetched(true);

            return couponData;
        } catch (error) {
            console.error("(useFetchSubscriptionPlans) -> fetchCouponDetails()", error);
            setCoupon(null); // <-- ADDED: Reset the coupon state on error
            setCouponFetched(true);
            return null;
        }
    };

    // The main useEffect hook to fetch data on component mount
    useEffect(() => {
        if (USE_HARDCODED_SUBSCRIPTION_PRICES) {
            setPrices(hardcodedPrices);
            setPricesFetched(true);
            setPricesError(null);
            return;
        }

        fetchProductPrices(stripeProductIds.subscription);
    }, [authUser, currentCoach?.id]);

    // UPDATED: Return the new flat state variables and the function
    return {
        prices,
        pricesFetched,
        pricesError,
        couponFetched,
        fetchCouponDetails,
    };
};

export const useFetchSubscriptionPlans = singletonHook(initState, useFetchSubscriptionPlansImpl);
